/* =============================================================================
   TBF FUNNEL — GA4 behavioural analytics.

   Companion to snippets/tbf-ga4.liquid, which ships the config and loads this.
   Read that file's header first: it explains why no ecommerce event is sent
   here, and why CTA attribution is done with a user property.

   EVENTS SENT (all custom, all `tbf_` prefixed, none ecommerce)
     tbf_view            once per session   landing: template, market, item, price
     tbf_section_view    once per section    where the page got read to
     tbf_cta_view        once per CTA        the button entered the viewport
     tbf_cta_click       every click         which button was pressed
     tbf_faq_open        every open          the objection being answered
     tbf_proof_click     every click         interest in transformations
     tbf_bnpl_view       once per session    the installment line was seen
     tbf_bnpl_click      every click         payment friction engaged
     tbf_scroll_depth    once per milestone   25 / 50 / 75 / 100%
     tbf_nav_click       every click         which page they left toward

   WHY CLICKS ARE OBSERVED IN THE CAPTURE PHASE
     assets/tbf-brand.js calls stopImmediatePropagation() on a repeat click of
     an add-to-cart button to block the double submit. It binds on document in
     the bubble phase; binding here in the capture phase means these listeners
     always run first, so a blocked second click is still seen — and then
     discarded by the busy check below, so it is not double counted.

   DEBUG
     Append ?tbf_ga_debug=1 to the URL for console output. Every event is also
     appended to window.__tbfGaLog (capped at 50) whether or not debug is on,
     so QA can read the fired sequence from the console.
============================================================================= */
(function () {
  var cfgEl = document.getElementById('tbf-ga4-config');
  if (!cfgEl) return;

  var CFG;
  try {
    CFG = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }

  var GA4 = CFG.ga4_id || 'G-Y0VT2QGQW7';
  var DEBUG = /[?&]tbf_ga_debug=1/.test(window.location.search);
  var T0 = Date.now();
  var SCROLL_MILESTONES = [25, 50, 75, 100];
  var PENDING = [];
  var FLUSH_TIMER = null;
  var FLUSH_ATTEMPTS = 0;

  window.__tbfGaLog = window.__tbfGaLog || [];

  /* ---------------------------------------------------------------------------
     Dispatch.

     gtag can still be loading when this file runs: Shopify's Google web pixel
     injects gtag.js asynchronously, and this script is deferred but may beat
     it. Rather than guess at when gtag becomes available, buffer and retry.
     Pushing a GTM-style object here would be wrong — the GTM container in
     layout/theme.liquid is commented out, so gtag.js would never read it.
  --------------------------------------------------------------------------- */
  function flush() {
    if (typeof window.gtag !== 'function') return false;
    while (PENDING.length) {
      var item = PENDING.shift();
      try {
        window.gtag('event', item[0], item[1]);
      } catch (e) {}
    }
    return true;
  }

  function scheduleFlush() {
    if (FLUSH_TIMER || FLUSH_ATTEMPTS > 50) return;
    FLUSH_TIMER = window.setTimeout(function () {
      FLUSH_TIMER = null;
      FLUSH_ATTEMPTS++;
      if (!flush()) scheduleFlush();
    }, 200);
  }

  function send(name, params) {
    var payload = {};
    if (params) {
      for (var k in params) {
        if (!Object.prototype.hasOwnProperty.call(params, k)) continue;
        if (params[k] === undefined || params[k] === null || params[k] === '') continue;
        payload[k] = params[k];
      }
    }
    /* Restrict to the GA4 property. The automatic ecommerce events carry the
       Google Ads conversion labels; these custom behaviour events should not
       be routed to Ads as though they were conversions. */
    payload.send_to = GA4;

    window.__tbfGaLog.push({ t: Date.now() - T0, event: name, params: payload });
    if (window.__tbfGaLog.length > 50) window.__tbfGaLog.shift();
    if (DEBUG) {
      try {
        window.console && window.console.log('[tbf-ga4]', name, payload);
      } catch (e) {}
    }

    if (typeof window.gtag === 'function') {
      try {
        window.gtag('event', name, payload);
      } catch (e) {}
    } else {
      PENDING.push([name, payload]);
      scheduleFlush();
    }
  }

  /* ---------------------------------------------------------------------------
     Params shared by every event, so any single event can be placed on the
     funnel without a join.
  --------------------------------------------------------------------------- */
  function base() {
    return {
      tbf_page: CFG.page_key,
      tbf_market: CFG.market,
      tbf_item_id: CFG.item_id,
      tbf_elapsed_ms: Date.now() - T0,
    };
  }

  /* Once per session, not once per page view: these describe "did this visitor
     reach this section", and a refresh should not multiply the denominator. */
  function onceStore(key) {
    var k = 'tbf_ga_' + CFG.page_key + '_' + key;
    try {
      if (window.sessionStorage.getItem(k)) return false;
      window.sessionStorage.setItem(k, '1');
      return true;
    } catch (e) {
      return true;
    }
  }

  /* ---------------------------------------------------------------------------
     Landing.
  --------------------------------------------------------------------------- */
  if (onceStore('view')) {
    send('tbf_view', Object.assign(base(), {
      tbf_item_name: CFG.item_name,
      tbf_bundle_price: CFG.bundle_price,
      tbf_referrer: document.referrer ? document.referrer.split('/')[2] : '(direct)',
    }));
  } else {
    /* Still flush for a repeat view so buffered nothing-times-out cleanly. */
    scheduleFlush();
  }

  /* ---------------------------------------------------------------------------
     Section reach.

     Names are derived from the section wrapper ids Shopify emits
     (`shopify-section-template--21753179603130__tbf_value` -> `tbf_value`), so
     no per-section theme edit is needed and the labels match the section names
     used everywhere else in the theme.

     The observer root is narrowed to the middle 20% of the viewport instead of
     using a visibility threshold. Thresholds do not work here: tbf_proof is over
     3500px tall, so a 50% threshold can never be met on a phone and the section
     would silently never report.
  --------------------------------------------------------------------------- */
  function sectionName(el) {
    var id = el.getAttribute('id') || '';
    var m = id.match(/__([A-Za-z0-9_-]+)$/);
    if (m) return m[1];
    return id.replace(/^shopify-section-/, '') || 'unknown';
  }

  function initObservers() {
    if (typeof window.IntersectionObserver !== 'function') return;

    /* The sticky site header is excluded deliberately. It is pinned to the top
       of the viewport, so it can never enter the middle band this observer
       watches — it would only ever inflate tbf_sections_total and leave a
       section that reports nothing. Its taps are covered by tbf_nav_click. */
    var sections = [].slice.call(document.querySelectorAll('[id^="shopify-section-"]')).filter(function (el) {
      return !/__tbf_header/.test(el.getAttribute('id') || '');
    });
    var total = sections.length;

    var sectionIo = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;
        var el = entry.target;
        var name = el.getAttribute('data-tbf-ga-section') || sectionName(el);
        sectionIo.unobserve(el);
        if (!onceStore('sec_' + name)) continue;
        send('tbf_section_view', Object.assign(base(), {
          tbf_section: name,
          tbf_section_index: Number(el.getAttribute('data-tbf-ga-section-index') || 0),
          tbf_sections_total: total,
        }));
      }
    }, { rootMargin: '-40% 0px -40% 0px', threshold: 0 });

    for (var i = 0; i < sections.length; i++) {
      sections[i].setAttribute('data-tbf-ga-section', sectionName(sections[i]));
      sections[i].setAttribute('data-tbf-ga-section-index', String(i + 1));
      sectionIo.observe(sections[i]);
    }

    /* CTA impressions. Separating "never saw the button" from "saw it and did
       not press it" is the difference between a copy problem and a placement
       problem, and GA4 cannot report it on its own. */
    var ctaIo = new IntersectionObserver(function (entries) {
      for (var j = 0; j < entries.length; j++) {
        var e2 = entries[j];
        if (!e2.isIntersecting) continue;
        var btn = e2.target;
        ctaIo.unobserve(btn);
        var src = btn.getAttribute('data-tbf-ga-source');
        if (!src || !onceStore('cta_' + src)) continue;
        send('tbf_cta_view', Object.assign(base(), {
          tbf_cta_section: src,
          tbf_cta_label: (btn.textContent || '').trim().slice(0, 80),
        }));
      }
    }, { rootMargin: '-40% 0px -40% 0px', threshold: 0 });

    var ctas = document.querySelectorAll('[data-tbf-ga-source]');
    for (var c = 0; c < ctas.length; c++) ctaIo.observe(ctas[c]);

    /* The installment line, once per session. */
    var bnpl = document.querySelector('[data-tbf-ga-bnpl]');
    if (bnpl) {
      var bnplIo = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (!entries[k].isIntersecting) continue;
          bnplIo.disconnect();
          if (!onceStore('bnpl')) return;
          send('tbf_bnpl_view', Object.assign(base(), {
            tbf_bnpl_interval: bnpl.getAttribute('data-tbf-ga-bnpl-interval'),
            tbf_bnpl_per: bnpl.getAttribute('data-tbf-ga-bnpl-per'),
          }));
        }
      }, { rootMargin: '-40% 0px -40% 0px', threshold: 0 });
      bnplIo.observe(bnpl);
    }
  }

  /* ---------------------------------------------------------------------------
     Clicks.
  --------------------------------------------------------------------------- */
  function isBusy(el) {
    /* tbf-brand.js marks a button it has already acted on. A repeat click is
       suppressed there; recognising that here keeps the click count honest. */
    if (!el) return false;
    return (
      el.getAttribute('aria-disabled') === 'true' ||
      el.getAttribute('aria-busy') === 'true' ||
      el.disabled === true
    );
  }

  function onDocumentClick(e) {
    var target = e.target;
    if (!target || !target.closest) return;
    /* The TBF funnel has no cart drawer, but the cart page can; ignore anything
       inside it so cart-side controls are not counted as page CTAs. */
    if (target.closest('.kozler-cart, .tbf-cart')) return;

    var cta = target.closest('[data-tbf-ga-source]');
    if (cta) {
      if (isBusy(cta)) return;
      var src = cta.getAttribute('data-tbf-ga-source');
      send('tbf_cta_click', Object.assign(base(), {
        tbf_cta_section: src,
        tbf_cta_label: (cta.textContent || '').trim().slice(0, 80),
      }));
      /* Last-click model. Attached to the automatic add_to_cart, begin_checkout
         and purchase that follow, so the whole funnel can be split by the
         button that started it. See the caveat in tbf-ga4.liquid. */
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('set', 'user_properties', {
            tbf_last_cta: src,
            tbf_last_cta_page: CFG.page_key,
          });
        }
      } catch (err) {}
      return;
    }

    var proof = target.closest('[data-tbf-ga-click]');
    if (proof) {
      var a = proof.closest('a') || proof;
      send('tbf_proof_click', Object.assign(base(), {
        tbf_link_label: (a.getAttribute('data-tbf-ga-label') || a.textContent || '').trim().slice(0, 80),
        tbf_link_url: a.getAttribute('href') || '',
      }));
      return;
    }

    var bnplEl = target.closest('[data-tbf-ga-bnpl]');
    if (bnplEl) {
      send('tbf_bnpl_click', base());
      return;
    }

    /* Navigation, including the footer the page ends on. `tbf_nav_region`
       separates a header tap from a footer tap: a footer click is usually the
       end of the visit, a header click usually is not. */
    var link = target.closest('a[href]');
    if (!link) return;
    var header = link.closest('header, [id*="__tbf_header"]');
    var footer = link.closest('footer, [id*="__tbf_footer"]');
    if (!header && !footer) return;
    var href = link.getAttribute('href') || '';
    if (href.charAt(0) === '#') return;
    send('tbf_nav_click', Object.assign(base(), {
      tbf_nav_region: footer ? 'footer' : 'header',
      tbf_link_label: (link.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 80),
      tbf_link_url: href,
    }));
  }

  /* ---------------------------------------------------------------------------
     FAQ opens.

     `toggle` does not bubble, but a capture-phase listener on document still
     receives it, which is why this is registered with `true`.
  --------------------------------------------------------------------------- */
  function onToggle(e) {
    var el = e.target;
    if (!el || !el.matches || !el.matches('details[data-tbf-ga-faq]')) return;
    if (!el.open) return;
    /* Chrome fires `toggle` for an initially-open detail during parsing; that is
       not a visitor action, so ignore anything in the first moments. */
    if (Date.now() - T0 < 500) return;
    send('tbf_faq_open', Object.assign(base(), {
      tbf_faq_question: el.getAttribute('data-tbf-ga-faq-q') || '',
      tbf_faq_index: el.getAttribute('data-tbf-ga-faq-i') || '',
    }));
  }

  /* ---------------------------------------------------------------------------
     Scroll depth. Section reach says what was read; this says how far the page
     was committed to, and survives sections that intersect the middle band
     without ever being read.
  --------------------------------------------------------------------------- */
  var scrollTick = false;
  var seenDepth = {};
  function onScroll() {
    if (scrollTick) return;
    scrollTick = true;
    window.requestAnimationFrame(function () {
      scrollTick = false;
      var doc = document.documentElement;
      var scrollable = Math.max(doc.scrollHeight - window.innerHeight, 1);
      var pct = Math.min(100, Math.round(((window.scrollY || doc.scrollTop || 0) / scrollable) * 100));
      for (var i = 0; i < SCROLL_MILESTONES.length; i++) {
        var m = SCROLL_MILESTONES[i];
        if (pct < m || seenDepth[m]) continue;
        seenDepth[m] = true;
        send('tbf_scroll_depth', Object.assign(base(), { tbf_percent: m }));
      }
    });
  }

  function init() {
    initObservers();
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('toggle', onToggle, true);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
